import axios from 'axios';
import { API_CONFIG } from '../config/api';
import { UserRole } from '../types/user';
import { InterviewerFilter, InterviewerListResponse, InterviewerCard } from '../types/interviewer';
import { ProfileResponse, ProfileUpdateRequest } from '../types/profile';
import { Booking, TimeSlot, BookingOperationResponse, SuccessResponse } from '../types/booking';
import { PaymentIntentResponse } from '../types/payment';

// Set up axios defaults
axios.defaults.baseURL = API_CONFIG.baseURL;

// Add auth token to requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for handling errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('token');
      
      // Only redirect to login if we have a token (meaning it's likely expired)
      // If no token, let the component handle the 401 appropriately
      if (token) {
        // Check if token is actually expired by trying to parse it
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Date.now() / 1000;
          
          // If token is expired, remove it and redirect
          if (payload.exp && payload.exp < currentTime) {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        } catch (_e) {
          // If token is malformed, remove it and redirect
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface RegisterRequest {
  email: string;
  password: string;
  role: UserRole;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface GoogleAuthRequest {
  token: string;
}

// Remove this interface as it's now defined in types/interviewer.ts

export interface BookingRequest {
  timeSlotId: number;
}

export interface Review {
  id: string;
  bookingId: string;
  interviewerId: string;
  intervieweeId: string;
  rating: number;
  comment: string;
  createdAt: Date;
}

export interface CreateReviewRequest {
  interviewerId: string;
  rating: number;
  comment: string;
}

class ApiService {
  // Profile endpoints
  profile = {
    getProfile: async (): Promise<ProfileResponse> => {
      const response = await axios.get(API_CONFIG.endpoints.profile.get);
      return response.data;
    },

    updateProfile: async (data: ProfileUpdateRequest, file?: File): Promise<ProfileResponse> => {
      const formData = new FormData();
      
      // Append all profile data to FormData
      if (data.profileName) formData.append('profileName', data.profileName);
      if (data.mobile) formData.append('mobile', data.mobile);
      if (data.country) formData.append('country', data.country);
      if (data.linkedinUrl) formData.append('linkedinUrl', data.linkedinUrl);
      if (data.specialty) formData.append('specialty', data.specialty);
      if (data.company) formData.append('company', data.company);
      if (data.experience) formData.append('experience', data.experience);
      if (data.purpose) formData.append('purpose', data.purpose);
      if (data.profilePictureUrl) formData.append('profilePictureUrl', data.profilePictureUrl);
      
      // Append languages array
      if (data.languages && data.languages.length > 0) {
        data.languages.forEach(language => {
          formData.append('languages', language);
        });
      }
      
      // Append file if provided
      if (file) {
        formData.append('file', file);
      }

      console.log('📤 FormData being sent to backend:');
      for (const [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      const response = await axios.post(API_CONFIG.endpoints.profile.update, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },

    uploadPicture: async (file: File): Promise<string> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(API_CONFIG.endpoints.profile.uploadPicture, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    },

    isProfileComplete: async (): Promise<{ isComplete: boolean }> => {
      const response = await axios.get('/api/profile/complete');
      return response.data;
    },
  };

  // Interviewer endpoints
  interviewers = {
    list: async (): Promise<InterviewerListResponse> => {
      const response = await axios.get(API_CONFIG.endpoints.interviewers.list);
      return response.data;
    },

    filter: async (filters: InterviewerFilter): Promise<InterviewerListResponse> => {
      // Transform frontend filter to backend format
      const backendFilter = {
        languages: filters.languages || [],
        specialty: (filters.specialty && filters.specialty.length > 0) ? filters.specialty[0] : '', // Backend expects single specialty for now
        availableFrom: '',
        availableTo: '',
        page: 1,
        pageSize: 12
      };
      
      const response = await axios.post(API_CONFIG.endpoints.interviewers.filter, backendFilter);
      return response.data;
    },

    getById: async (id: string): Promise<InterviewerCard> => {
      const response = await axios.get(API_CONFIG.endpoints.interviewers.getById.replace(':id', id));
      return response.data;
    },

    getAvailableLanguages: async (): Promise<string[]> => {
      const response = await axios.get(API_CONFIG.endpoints.interviewers.availableLanguages);
      return response.data;
    },

    getAvailableSpecialties: async (): Promise<string[]> => {
      // Get all interviewers and extract unique specialties
      const response = await axios.get(API_CONFIG.endpoints.interviewers.list);
      const interviewers = response.data.interviewers || response.data;
      
      // Extract unique specialties from the interviewer list
      const specialties = [...new Set(
        interviewers
          .map((interviewer: any) => interviewer.specialty as string)
          .filter((specialty: string) => specialty && specialty.trim() !== '')
      )] as string[];
      
      return specialties.sort();
    },
  };

  // Booking endpoints
  bookings = {
    create: async (data: BookingRequest): Promise<Booking> => {
      const response = await axios.post(API_CONFIG.endpoints.bookings.create, data);
      return response.data;
    },

    createBooking: async (timeSlotId: number): Promise<BookingOperationResponse> => {
      const response = await axios.post(API_CONFIG.endpoints.bookings.create, {
        timeSlotId,
      });
      return response.data;
    },

    createPaymentIntent: async (
      timeSlotId: number, 
      selectedCurrency?: string, 
      selectedAmount?: number
    ): Promise<PaymentIntentResponse> => {
      const payload: any = { timeSlotId };
      
      // Add optional currency selection for Georgian users
      if (selectedCurrency) {
        payload.selectedCurrency = selectedCurrency;
      }
      if (selectedAmount) {
        payload.selectedAmount = selectedAmount;
      }
      
      const response = await axios.post(API_CONFIG.endpoints.bookings.createPaymentIntent, payload);
      return response.data;
    },

    list: async (): Promise<Booking[]> => {
      const response = await axios.get(API_CONFIG.endpoints.bookings.list);
      return response.data;
    },

    listForInterviewer: async (): Promise<Booking[]> => {
      const response = await axios.get(API_CONFIG.endpoints.bookings.listForInterviewer);
      return response.data;
    },

    listForInterviewee: async (): Promise<Booking[]> => {
      const response = await axios.get(API_CONFIG.endpoints.bookings.listForInterviewee);
      return response.data;
    },

    cancel: async (bookingId: string): Promise<SuccessResponse> => {
      const response = await axios.post(API_CONFIG.endpoints.bookings.cancel.replace(':id', bookingId));
      return response.data;
    },

    accept: async (bookingId: string): Promise<SuccessResponse> => {
      const response = await axios.post(API_CONFIG.endpoints.bookings.accept.replace(':id', bookingId));
      return response.data;
    },

    decline: async (bookingId: string): Promise<SuccessResponse> => {
      const response = await axios.post(API_CONFIG.endpoints.bookings.decline.replace(':id', bookingId));
      return response.data;
    },

    getById: async (bookingId: string): Promise<Booking> => {
      const response = await axios.get(API_CONFIG.endpoints.bookings.getById.replace(':id', bookingId));
      return response.data;
    },

    addMeetingLink: async (bookingId: string, meetingLink: string): Promise<Booking> => {
      const response = await axios.post(API_CONFIG.endpoints.bookings.addMeetingLink.replace(':id', bookingId), {
        meetingLink,
      });
      return response.data;
    },

    uploadRecording: async (bookingId: string, recordingFile: File): Promise<SuccessResponse> => {
      const formData = new FormData();
      formData.append('recordingFile', recordingFile);
      
      const response = await axios.post(`/api/booking/${bookingId}/recording`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
  };

  // TimeSlot endpoints
  timeSlots = {
    getAvailableTimeSlots: async (interviewerId: number): Promise<TimeSlot[]> => {
      const response = await axios.get(`/api/TimeSlot/available/${interviewerId}`);
      return response.data;
    },

    getMyTimeSlots: async (): Promise<TimeSlot[]> => {
      const response = await axios.get('/api/TimeSlot/Interviewer');
      return response.data;
    },

    createBatch: async (timeSlots: Array<{ startTime: string; endTime: string; amount?: number; currency?: string }>): Promise<TimeSlot[]> => {
      const response = await axios.post('/api/TimeSlot/add-batch', {
        TimeSlots: timeSlots.map(slot => ({
          StartTime: slot.startTime,
          EndTime: slot.endTime,
          Amount: slot.amount,
          Currency: slot.currency || 'USD',
        })),
      });
      return response.data;
    },

    delete: async (slotId: number): Promise<void> => {
      await axios.delete(`/api/TimeSlot/${slotId}`);
    },

    update: async (slotId: number, data: { startTime: string; endTime: string; amount?: number; currency?: string }): Promise<TimeSlot> => {
      const response = await axios.put(`/api/TimeSlot/${slotId}`, {
        StartTime: data.startTime,
        EndTime: data.endTime,
        Amount: data.amount,
        Currency: data.currency || 'USD',
      });
      return response.data;
    },
  };

  // Review endpoints
  reviews = {
    create: async (data: CreateReviewRequest): Promise<Review> => {
      const response = await axios.post(API_CONFIG.endpoints.reviews.create, data);
      return response.data;
    },

    listForInterviewer: async (interviewerId: string): Promise<Review[]> => {
      const response = await axios.get(API_CONFIG.endpoints.reviews.listForInterviewer.replace(':id', interviewerId));
      return response.data;
    },
  };
}

export const apiService = new ApiService(); 